/**
 * RoomErrorBoundary (PPL-42) — catches render errors in any mediation
 * page/route so a throw doesn't crash the entire app shell.
 *
 * Uses inline styles to stay dependency-free (no extra CSS file, no
 * framework). Matches the dark + purple accent aesthetic of the mediation
 * pages. Tapping "try again" resets the boundary state so the failed subtree
 * re-mounts cleanly.
 */

import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

export default class RoomErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    const message =
      error instanceof Error ? error.message : 'unexpected error';
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error('[RoomErrorBoundary] render error', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={orbStyle} aria-hidden="true" />
          <p style={titleStyle}>something went wrong</p>
          <p style={bodyStyle}>
            this room screen hit an unexpected error. your progress is
            safe — tap below to try again.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            style={buttonStyle}
            onMouseEnter={e =>
              ((e.currentTarget as HTMLButtonElement).style.opacity = '0.85')
            }
            onMouseLeave={e =>
              ((e.currentTarget as HTMLButtonElement).style.opacity = '1')
            }
          >
            try again
          </button>
        </div>
      </div>
    );
  }
}

// ─── Styles (inline — no extra CSS file needed) ───────────────────────────────

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100%',
  padding: '48px 24px',
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  maxWidth: '380px',
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '20px',
  padding: '36px 28px',
  textAlign: 'center',
};

const orbStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #b84fff 0%, #ff4fa3 100%)',
  opacity: 0.6,
  flexShrink: 0,
  marginBottom: '4px',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '17px',
  fontFamily: 'GeneralSans-Semibold, system-ui, sans-serif',
  color: '#ffffff',
  letterSpacing: '-0.01em',
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '14px',
  fontFamily: 'GeneralSans-Regular, system-ui, sans-serif',
  color: 'rgba(255,255,255,0.6)',
  lineHeight: 1.5,
};

const buttonStyle: React.CSSProperties = {
  marginTop: '8px',
  appearance: 'none',
  border: 'none',
  cursor: 'pointer',
  background: 'linear-gradient(135deg, #b84fff 0%, #ff4fa3 100%)',
  color: '#ffffff',
  fontFamily: 'GeneralSans-Semibold, system-ui, sans-serif',
  fontSize: '14px',
  letterSpacing: '0.01em',
  borderRadius: '12px',
  padding: '10px 24px',
  transition: 'opacity 0.15s ease',
};
